import { FC, useMemo } from 'react';
import styles from './AppliedFilterSummary.module.css';
import { useCategories } from '../hooks/useCategories';
import { AppliedFilterChip, BrowseFilterState, getAppliedFilterChips } from '../utils/filterUtils';

interface AppliedFilterSummaryProps {
  filters: BrowseFilterState;
  searchQuery?: string;
  onRemoveChip: (chip: AppliedFilterChip) => void;
  onClearSearchQuery: () => void;
  onClearAll: () => void;
}

const AppliedFilterSummary: FC<AppliedFilterSummaryProps> = ({
  filters,
  searchQuery = '',
  onRemoveChip,
  onClearSearchQuery,
  onClearAll,
}) => {
  const { categories } = useCategories();

  const categoryNameBySlug = useMemo(() => {
    return new Map(categories.map((category) => [category.slug, category.name]));
  }, [categories]);

  const chips = useMemo(() => {
    return getAppliedFilterChips(filters, categoryNameBySlug);
  }, [filters, categoryNameBySlug]);

  const normalizedSearchQuery = searchQuery.trim();
  const showSearchChip = normalizedSearchQuery.length > 0 && normalizedSearchQuery !== filters.venue.trim();

  if (chips.length === 0 && !showSearchChip) {
    return null;
  }

  return (
    <div className={styles.summary} aria-label="Filtres actifs">
      <div className={styles.chips}>
        {showSearchChip && (
          <button
            type="button"
            className={styles.chip}
            onClick={onClearSearchQuery}
            aria-label={`Retirer la recherche ${normalizedSearchQuery}`}
          >
            <span>&quot;{normalizedSearchQuery}&quot;</span>
            <span className={styles.chipClose} aria-hidden="true">×</span>
          </button>
        )}
        {chips.map((chip) => (
          <button
            key={`${chip.key}-${chip.value ?? chip.label}`}
            type="button"
            className={styles.chip}
            onClick={() => onRemoveChip(chip)}
            aria-label={`Retirer le filtre ${chip.label}`}
          >
            <span>{chip.label}</span>
            <span className={styles.chipClose} aria-hidden="true">×</span>
          </button>
        ))}
      </div>
      <button type="button" className={styles.clearAll} onClick={onClearAll}>
        Tout effacer
      </button>
    </div>
  );
};

export default AppliedFilterSummary;
